/*
 * Runtime bindings for Manual forms on visual pages.
 * Text elements can become inputs, buttons can submit the shared draft, and
 * iframe pages can use the same draft through a constrained postMessage bridge.
 */
(function attachManualFormRuntime(globalScope, factory) {
  const manualFormDependency = globalScope && globalScope.ManualForm
    ? globalScope.ManualForm
    : (typeof module === 'object' && module.exports ? require('./manual-form') : null)
  const api = factory(
    manualFormDependency,
    globalScope
  )

  if (typeof module === 'object' && module.exports) module.exports = api
  if (globalScope) globalScope.ManualFormRuntime = api
})(typeof globalThis !== 'undefined' ? globalThis : this, function createModule(ManualForm, globalScope) {
  'use strict'

  const MESSAGE_TYPES = Object.freeze({
    READY: 'datarapor.manual-form.ready',
    SET_VALUE: 'datarapor.manual-form.set-value',
    GET_STATE: 'datarapor.manual-form.get-state',
    SUBMIT: 'datarapor.manual-form.submit',
    RESULT: 'datarapor.manual-form.result'
  })

  const SUPPORTED_ROLES = new Set(['input', 'submit', 'iframe'])

  function positiveFormId(value) {
    const formId = Number(value)
    if (!Number.isInteger(formId) || formId <= 0) throw new Error('Geçerli bir Manual Form ID seçin.')
    return formId
  }

  function normalizedTagName(value) {
    const tagName = String(value || '').trim()
    if (!tagName) throw new Error('Manual tag adı zorunludur.')
    return tagName
  }

  function bindingFromElement(element) {
    const role = String(element && element.manualBindingRole || '').trim().toLowerCase()
    if (!SUPPORTED_ROLES.has(role)) return null
    return {
      role,
      formId: positiveFormId(element.manualFormId),
      tagName: role === 'input' ? normalizedTagName(element.manualTagName) : '',
      dataType: String(element.manualDataType || 'String'),
      placeholder: String(element.manualPlaceholder || element.staticText || ''),
      confirm: element.manualSubmitConfirm === true || element.manualSubmitConfirm === 'true'
    }
  }

  function defaultRequest(url, options) {
    if (globalScope && globalScope.Auth && typeof globalScope.Auth.authenticatedFetch === 'function') {
      return globalScope.Auth.authenticatedFetch(url, options)
    }
    return globalScope.fetch(url, options)
  }

  function defaultNotify(message, type = 'info', title = 'Manuel Form') {
    if (globalScope && typeof globalScope.showToast === 'function') {
      try {
        globalScope.showToast(message, { type, duration: 3500, title })
        return
      } catch (_) { /* use fallback below */ }
    }
    if (type === 'danger') console.error(`[${title}] ${message}`)
    else console.info(`[${title}] ${message}`)
  }

  async function parseResponse(response) {
    const text = await response.text()
    if (!text) return null
    try { return JSON.parse(text) } catch (_) { return { message: text } }
  }

  function payloadError(status, payload) {
    if (ManualForm && typeof ManualForm.errorMessage === 'function') return ManualForm.errorMessage(status, payload)
    return String(payload && (payload.message || payload.detail || payload.error) || `İstek başarısız oldu (HTTP ${status}).`)
  }

  function recordValues(record) {
    if (ManualForm && typeof ManualForm.recordValues === 'function') return ManualForm.recordValues(record)
    const values = record && (record.values || record.tag_values || record.data)
    return values && typeof values === 'object' && !Array.isArray(values) ? { ...values } : {}
  }

  class RuntimeController {
    constructor(options = {}) {
      this.request = options.request || defaultRequest
      this.notify = options.notify || defaultNotify
      this.drafts = new Map()
      this.latest = new Map()
      this.latestLoads = new Map()
      this.trackers = new Map()
      this.iframes = new Set()
      this.messageHandler = event => this.handleMessage(event)
      if (globalScope && typeof globalScope.addEventListener === 'function') {
        globalScope.addEventListener('message', this.messageHandler)
      }
    }

    draftFor(formId) {
      const id = positiveFormId(formId)
      if (!this.drafts.has(id)) this.drafts.set(id, new Map())
      return this.drafts.get(id)
    }

    setValue(formId, tagName, rawValue, dataType = 'String') {
      const id = positiveFormId(formId)
      const name = normalizedTagName(tagName)
      const draft = this.draftFor(id)
      if (rawValue === '' || rawValue === null || rawValue === undefined) draft.delete(name)
      else draft.set(name, { rawValue, dataType: String(dataType || 'String') })
      this.broadcastState(id)
      return this.state(id)
    }

    state(formId) {
      const id = positiveFormId(formId)
      const draft = {}
      this.draftFor(id).forEach((entry, name) => { draft[name] = entry.rawValue })
      return { form_id: id, latest_values: { ...(this.latest.get(id) || {}) }, draft_values: draft }
    }

    trackerFor(formId) {
      const id = positiveFormId(formId)
      if (!this.trackers.has(id)) {
        const Tracker = ManualForm && ManualForm.IdempotencyTracker
        if (!Tracker) throw new Error('Manual form idempotency servisi yüklenemedi.')
        this.trackers.set(id, new Tracker())
      }
      return this.trackers.get(id)
    }

    buildPayload(formId, time) {
      const id = positiveFormId(formId)
      const values = []
      this.draftFor(id).forEach((entry, tagName) => {
        const value = ManualForm && typeof ManualForm.coerceValue === 'function'
          ? ManualForm.coerceValue(entry.dataType, entry.rawValue)
          : entry.rawValue
        values.push({ tag_name: tagName, value })
      })
      if (!values.length) throw new Error('Göndermek için en az bir manuel tag değeri girin.')
      const localTime = time || (ManualForm && ManualForm.localDateTimeValue ? ManualForm.localDateTimeValue() : '')
      return {
        time: ManualForm && ManualForm.localDateTimeToOffsetIso
          ? ManualForm.localDateTimeToOffsetIso(localTime)
          : new Date().toISOString(),
        values
      }
    }

    async submit(formId, options = {}) {
      const id = positiveFormId(formId)
      const payload = this.buildPayload(id, options.time)
      const tracker = this.trackerFor(id)
      const key = tracker.keyFor(payload)
      const response = await this.request(`/api/v1/forms/${encodeURIComponent(id)}/manual-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'Idempotency-Key': key },
        body: JSON.stringify(payload)
      })
      const result = await parseResponse(response)
      if (!response.ok) throw new Error(payloadError(response.status, result))
      tracker.clear()
      this.drafts.delete(id)
      const record = result && (result.record || result.data || result)
      const values = recordValues(record)
      this.latest.set(id, Object.keys(values).length ? values : Object.fromEntries(payload.values.map(item => [item.tag_name, item.value])))
      this.broadcastState(id)
      if (globalScope && typeof globalScope.dispatchEvent === 'function' && typeof globalScope.CustomEvent === 'function') {
        globalScope.dispatchEvent(new globalScope.CustomEvent('datarapor:manual-record-saved', { detail: { form_id: id, result } }))
      }
      return result
    }

    async loadLatest(formId, options = {}) {
      const id = positiveFormId(formId)
      if (!options.force && this.latest.has(id)) return { ...this.latest.get(id) }
      if (!options.force && this.latestLoads.has(id)) return this.latestLoads.get(id)
      const loadPromise = this.fetchLatest(id)
      this.latestLoads.set(id, loadPromise)
      try {
        return await loadPromise
      } finally {
        this.latestLoads.delete(id)
      }
    }

    async fetchLatest(id) {
      const response = await this.request(`/api/v1/forms/${encodeURIComponent(id)}/manual-records?limit=1&offset=0`, {
        headers: { Accept: 'application/json' }
      })
      const payload = await parseResponse(response)
      if (!response.ok) throw new Error(payloadError(response.status, payload))
      const normalized = ManualForm && ManualForm.normalizeListResponse
        ? ManualForm.normalizeListResponse(payload, 1, 0)
        : { records: Array.isArray(payload) ? payload : [] }
      const record = ManualForm && ManualForm.latestRecord
        ? ManualForm.latestRecord(normalized.records)
        : normalized.records[0]
      const values = recordValues(record)
      this.latest.set(id, values)
      this.broadcastState(id)
      return values
    }

    attachElement(domElement, element) {
      let binding
      try { binding = bindingFromElement(element) } catch (error) {
        domElement.title = error.message
        domElement.dataset.manualBindingError = error.message
        return false
      }
      if (!binding) return false
      domElement.dataset.manualBindingRole = binding.role
      domElement.dataset.manualFormId = String(binding.formId)

      if (binding.role === 'input') this.attachInput(domElement, binding)
      else if (binding.role === 'submit') this.attachSubmit(domElement, binding)
      else if (binding.role === 'iframe' && domElement.tagName && domElement.tagName.toLowerCase() === 'iframe') this.attachIframe(domElement, binding)
      return true
    }

    attachInput(host, binding) {
      host.onclick = null
      const input = binding.dataType === 'Bool' ? document.createElement('select') : document.createElement('input')
      if (binding.dataType === 'Bool') {
        input.innerHTML = '<option value="">Seçin</option><option value="true">true</option><option value="false">false</option>'
      } else {
        input.type = ManualForm && ManualForm.NUMERIC_TYPES && ManualForm.NUMERIC_TYPES.has(binding.dataType) ? 'number' : 'text'
        if (input.type === 'number') input.step = 'any'
        input.placeholder = binding.placeholder
      }
      input.className = 'manual-form-bound-input'
      input.setAttribute('aria-label', binding.placeholder || binding.tagName)
      input.style.cssText = 'width:100%;height:100%;box-sizing:border-box;background:inherit;color:inherit;border:inherit;border-radius:inherit;padding:0.45rem 0.6rem;font:inherit;text-align:inherit;outline:none;'
      const update = () => this.setValue(binding.formId, binding.tagName, input.value, binding.dataType)
      input.addEventListener('input', update)
      input.addEventListener('change', update)
      host.innerHTML = ''
      host.appendChild(input)
      host.style.pointerEvents = 'auto'
      this.loadLatest(binding.formId).then(values => {
        const draftEntry = this.draftFor(binding.formId).get(binding.tagName)
        if (draftEntry) {
          input.value = String(draftEntry.rawValue)
        } else if (values[binding.tagName] !== undefined) {
          input.value = String(values[binding.tagName])
        }
      }).catch(error => { host.title = error.message })
    }

    attachSubmit(button, binding) {
      button.onclick = null
      button.style.pointerEvents = 'auto'
      button.style.cursor = 'pointer'
      button.addEventListener('click', async event => {
        event.preventDefault()
        event.stopPropagation()
        if (button.dataset.manualSubmitting === 'true') return
        if (binding.confirm && !globalScope.confirm('Manuel form kaydı gönderilsin mi?')) return
        button.dataset.manualSubmitting = 'true'
        button.style.opacity = '0.65'
        try {
          const result = await this.submit(binding.formId)
          const replay = Boolean(result && (result.idempotent_replay || result.record && result.record.idempotent_replay))
          this.notify(replay ? 'Kayıt daha önce alınmıştı.' : 'Manuel kayıt başarıyla gönderildi.', replay ? 'info' : 'success')
        } catch (error) {
          this.notify(error.message || 'Manuel kayıt gönderilemedi.', 'danger')
        } finally {
          delete button.dataset.manualSubmitting
          button.style.opacity = ''
        }
      })
    }

    attachIframe(iframe, binding) {
      const entry = { iframe, formId: binding.formId }
      this.iframes.add(entry)
      iframe.addEventListener('load', () => {
        this.postToIframe(entry, { type: MESSAGE_TYPES.READY, form_id: binding.formId, state: this.state(binding.formId) })
        this.loadLatest(binding.formId).catch(() => {})
      })
    }

    iframeEntryFor(source) {
      this.iframes.forEach(entry => {
        if (!entry.iframe || !entry.iframe.isConnected) this.iframes.delete(entry)
      })
      return Array.from(this.iframes).find(entry => entry.iframe && entry.iframe.contentWindow === source) || null
    }

    postToIframe(entry, message) {
      if (!entry || !entry.iframe || !entry.iframe.contentWindow) return
      entry.iframe.contentWindow.postMessage(message, '*')
    }

    broadcastState(formId) {
      this.iframes.forEach(entry => {
        if (!entry.iframe || !entry.iframe.isConnected) {
          this.iframes.delete(entry)
          return
        }
        if (entry.formId === Number(formId)) {
          this.postToIframe(entry, { type: MESSAGE_TYPES.RESULT, action: 'state', ok: true, state: this.state(formId) })
        }
      })
    }

    async handleMessage(event) {
      const entry = this.iframeEntryFor(event.source)
      const message = event.data
      if (!entry || !message || typeof message !== 'object') return
      if (![MESSAGE_TYPES.SET_VALUE, MESSAGE_TYPES.GET_STATE, MESSAGE_TYPES.SUBMIT].includes(message.type)) return
      const response = { type: MESSAGE_TYPES.RESULT, request_id: message.request_id || null, action: message.type, ok: true }
      try {
        if (message.type === MESSAGE_TYPES.SET_VALUE) {
          response.state = this.setValue(entry.formId, message.tag_name, message.value, message.data_type || 'String')
        } else if (message.type === MESSAGE_TYPES.GET_STATE) {
          if (message.refresh === true) await this.loadLatest(entry.formId, { force: true })
          response.state = this.state(entry.formId)
        } else {
          response.result = await this.submit(entry.formId, { time: message.time })
          response.state = this.state(entry.formId)
        }
      } catch (error) {
        response.ok = false
        response.error = error.message || 'Manuel form işlemi başarısız.'
      }
      this.postToIframe(entry, response)
    }

    destroy() {
      if (globalScope && typeof globalScope.removeEventListener === 'function') {
        globalScope.removeEventListener('message', this.messageHandler)
      }
      this.iframes.clear()
    }
  }

  return Object.freeze({
    MESSAGE_TYPES,
    RuntimeController,
    bindingFromElement,
    normalizedTagName,
    positiveFormId
  })
})
